import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import { CheckCircle2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import { StepIdea } from '@/components/create/StepIdea'
import { StepScript } from '@/components/create/StepScript'
import { StepSetup } from '@/components/create/StepSetup'
import { StepGenerate } from '@/components/create/StepGenerate'
import type { SetupValues, VideoMode } from '@/components/create/StepSetup'
import { apiJson } from '@/lib/api'
import { useLocalStorageState, clearCreateAdDraft } from '@/hooks/useLocalStorageState'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

// Task 9: per-step hint shown on hover so users know what each step does
// before reaching it. Wrapped on the step buttons in the stepper below.
const STEPS: { id: number; label: string; hint: string }[] = [
  {
    id: 1,
    label: 'Idea',
    hint: 'Describe your ad in one or two sentences. Pick the video type (product / avatar / composite), attach reference images, choose a style. The AI writes the script from here.',
  },
  {
    id: 2,
    label: 'Script',
    hint: 'Review and edit the generated multi-scene script. You can tweak the hook, CTA, per-scene durations and prompts, or regenerate any single scene. Approve when ready.',
  },
  {
    id: 3,
    label: 'Setup',
    hint: 'Pick the video model, aspect ratio, voice (if avatar), and stitching options. Most fields auto-fill from Step 1 — you only touch what you want to change.',
  },
  {
    id: 4,
    label: 'Generate',
    hint: 'Kicks off generation. Each scene goes to the model in parallel; you watch progress in real time, then the assembly stitches into one final video.',
  },
]

const DEFAULT_SETUP: SetupValues = {
  avatarAssetId: null,
  refImageIds: [],
  contextFolderIds: [],
  styleReferenceIds: [],
  modelId: '',
  aspectRatio: '9:16',
  generationMode: 'product',
  videoType: 'product',
  videoMode: 'cinematic_product',
  autoStitch: true,
  transitionSeconds: 0.3,
  fastPreview: false,
  bridgeEnabled: false,
  audioUrl: '',
  voiceId: '',
  durationSeconds: 5,
  generationParams: {},
}

export function CreateAd() {
  const [searchParams, setSearchParams] = useSearchParams()

  const stepParam = Number(searchParams.get('step') ?? '1')
  const scriptIdParam = searchParams.get('script_id')
    ? Number(searchParams.get('script_id'))
    : null
  // Optional asset id arriving from Create Image → Storyboard's "Use in
  // Create Ad" button. Pre-attaches the storyboard as the primary ref.
  const storyboardAssetIdParam = searchParams.get('storyboard_asset_id')
    ? Number(searchParams.get('storyboard_asset_id'))
    : null

  const step = stepParam >= 1 && stepParam <= 4 ? stepParam : 1
  const scriptId = scriptIdParam
  // Persist setup across back-nav AND page refresh so a user who refreshes
  // mid-wizard doesn't lose their model / aspect / refs (Task 6). The
  // companion idea-stage fields are persisted inside StepIdea via the same
  // useLocalStorageState hook + creativeos.createAd.* key prefix. Both are
  // wiped together by clearCreateAdDraft() after a successful assembly.
  const [setup, setSetup] = useLocalStorageState<SetupValues>(
    'creativeos.createAd.setup',
    DEFAULT_SETUP,
  )
  // Total ad duration = sum of scene durations on the active script version.
  // Kept separate from setup.durationSeconds because that field doubles as
  // the per-call duration / cost-estimate in StepSetup; conflating them
  // causes StepSetup to flip back to 5s on model change.
  const [scriptTotalDuration, setScriptTotalDuration] = React.useState<number>(0)
  // Structured plan attached to the storyboard asset (when one was used
  // via ?storyboard_asset_id). Forwarded into the script POST so the
  // generator writes panel-aware Seedance prompts. null when no
  // storyboard is in play OR the attached asset has no plan (legacy).
  const [storyboardPlan, setStoryboardPlan] = React.useState<Record<string, unknown> | null>(null)

  // Note: previously this component reset setup to DEFAULT_SETUP whenever
  // there was no script_id in the URL. That was the "fresh start every
  // visit" behaviour. With localStorage-backed setup (Task 6), the draft
  // intentionally survives across visits — the user starts a new ad by
  // either clicking "Discard draft" in the UI or by clicking "Create Ad"
  // in the sidebar (which navigates to a clean /create — the URL-transition
  // effect below detects that and resets in-memory state + localStorage).

  // Wizard remount key — bumped when we hard-reset so child Step components
  // (StepIdea etc.) re-run their useLocalStorageState initializers against
  // freshly-cleared localStorage. Without this, their in-memory state
  // immediately rewrites the values we just removed.
  const [wizardKey, setWizardKey] = React.useState(0)

  // Detect "user clicked Create Ad in the sidebar while a draft was active".
  // We define a clean /create URL as: no step (or step=1), no script_id,
  // no storyboard_asset_id. When we transition INTO that state from a
  // non-clean URL, treat it as "start fresh" intent and wipe the draft.
  // On first mount with a clean URL we DO NOT reset — that preserves the
  // back-nav + refresh use case Task 6 introduced.
  const isCleanCreateUrl =
    (stepParam === 1 || Number.isNaN(stepParam)) && !scriptIdParam && !storyboardAssetIdParam
  const prevCleanRef = React.useRef<boolean>(isCleanCreateUrl)

  React.useEffect(() => {
    if (isCleanCreateUrl && !prevCleanRef.current) {
      clearCreateAdDraft()
      setSetup(DEFAULT_SETUP)
      setScriptTotalDuration(0)
      setStoryboardPlan(null)
      setWizardKey((k) => k + 1)
    }
    prevCleanRef.current = isCleanCreateUrl
  }, [isCleanCreateUrl, setSetup])

  // Storyboard handoff: when arriving with ?storyboard_asset_id=N (from the
  // Create Image → Storyboard tab), pre-attach that asset as the primary
  // ref so the user lands in step 1 with the storyboard already bound.
  // Also fetches the asset detail so we can pull asset.meta.storyboard_plan
  // and stash it for forwarding into the script POST.
  React.useEffect(() => {
    if (!storyboardAssetIdParam || scriptIdParam) return
    setSetup((prev) => {
      if (prev.refImageIds && prev.refImageIds.length > 0) return prev
      return {
        ...prev,
        avatarAssetId: storyboardAssetIdParam,
        refImageIds: [storyboardAssetIdParam],
        videoType: 'product',
      }
    })

    // Best-effort: fetch the asset and pull its structured plan.
    let cancelled = false
    ;(async () => {
      try {
        const asset = await apiJson<{ meta?: { storyboard_plan?: Record<string, unknown> } | null }>(
          `/api/v1/assets/${storyboardAssetIdParam}`,
        )
        if (cancelled) return
        const plan = asset?.meta?.storyboard_plan
        if (plan && typeof plan === 'object') {
          setStoryboardPlan(plan)
        }
      } catch {
        /* legacy assets won't have meta — silently leave plan null */
      }
    })()
    return () => { cancelled = true }
  }, [storyboardAssetIdParam, scriptIdParam])

  // Resume hydration: when landing on a script_id URL after a refresh,
  // setup state is empty (DEFAULT_SETUP). Fetch the script and pull the
  // attached refs from active_version.meta.reference_assets[].asset_id so
  // the ref grid + setup come back to what the user originally chose. Without
  // this, refs the user picked in step 1 disappear on every page refresh.
  React.useEffect(() => {
    if (!scriptIdParam) return
    let cancelled = false
    ;(async () => {
      try {
        type ScriptDetail = {
          source_prompt: string
          active_version?: {
            meta?: {
              reference_assets?: Array<{ asset_id?: number | null; tag?: string }>
              video_mode?: VideoMode
              total_duration?: number
            } | null
            scenes?: Array<{ duration_seconds?: number }>
          } | null
        }
        const data = await apiJson<ScriptDetail>(`/api/v1/scripts/${scriptIdParam}`)
        if (cancelled) return
        const refs = data.active_version?.meta?.reference_assets ?? []
        const refAssetIds = refs
          .map((r) => r.asset_id)
          .filter((id): id is number => typeof id === 'number')
        const persistedVideoMode = data.active_version?.meta?.video_mode
        // Total ad duration = sum of scene durations. Tracked in its own
        // state so StepGenerate can show the correct number ("30s") while
        // StepSetup keeps its per-call durationSeconds for cost estimates.
        const scenes = data.active_version?.scenes ?? []
        const totalDuration =
          scenes.reduce((acc, s) => acc + (s.duration_seconds ?? 0), 0) ||
          data.active_version?.meta?.total_duration ||
          0
        if (totalDuration > 0) {
          setScriptTotalDuration(totalDuration)
        }
        if (refAssetIds.length === 0 && !persistedVideoMode) return
        setSetup((prev) => {
          // Hydrate refs / videoMode only when setup is empty so we never
          // overwrite the user's in-session edits.
          const refsEmpty =
            (!prev.refImageIds || prev.refImageIds.length === 0) &&
            prev.avatarAssetId === null
          if (!refsEmpty) return prev
          const next = { ...prev }
          if (refAssetIds.length > 0) {
            next.refImageIds = refAssetIds
            next.avatarAssetId = refAssetIds[0]
            next.videoType = 'product'
          }
          if (persistedVideoMode) {
            next.videoMode = persistedVideoMode
          }
          return next
        })
      } catch {
        /* hydration is best-effort — surface nothing on failure, the user
           can re-pick refs manually if the fetch fails. */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [scriptIdParam])

  // Tracks whether StepScript currently has unsaved edits. Updated via the
  // onDirtyChange callback we pass down. A ref avoids re-rendering CreateAd
  // every time the user types in a script field.
  const scriptDirtyRef = React.useRef(false)
  const handleScriptDirtyChange = React.useCallback((dirty: boolean) => {
    scriptDirtyRef.current = dirty
  }, [])

  // Push URL for step transitions (browser back works). When stepping BACK
  // from the Script step with unsaved edits, confirm so a stray click doesn't
  // silently discard work (Task 6). The guard only fires on backward nav —
  // moving forward via Approve is intentional and should never prompt.
  const goTo = (nextStep: number, sid?: number | null) => {
    if (step === 2 && nextStep < step && scriptDirtyRef.current) {
      const ok = window.confirm(
        'You have unsaved script edits. Go back and discard them?',
      )
      if (!ok) return
      scriptDirtyRef.current = false
    }
    const resolvedScriptId = sid !== undefined ? sid : scriptId
    const params: Record<string, string> = { step: String(nextStep) }
    if (resolvedScriptId) params.script_id = String(resolvedScriptId)
    setSearchParams(params)
  }

  // Unified: always go to step 2 (script review). Avatar/product info pre-fills setup.
  const handleScriptCreated = (
    id: number,
    _quick: boolean,
    avatarMode?: boolean,
    avatarAssetId?: number | null,
    productId?: number | null,
    refImageIds?: number[],
    videoMode?: VideoMode,
  ) => {
    setSetup((prev) => {
      const next = { ...prev }
      // Always persist the reference image asset ID regardless of mode.
      // avatarMode drives the generationMode flag; avatarAssetId is the image reference for any mode.
      if (avatarAssetId) {
        next.avatarAssetId = avatarAssetId
      }
      // Persist all selected reference image ids for downstream use in StepGenerate.
      if (refImageIds && refImageIds.length > 0) {
        next.refImageIds = refImageIds
      }
      if (avatarMode) {
        next.generationMode = 'avatar' as const
      }
      // Derive videoType from what was selected in StepIdea
      if (avatarMode && avatarAssetId && productId) {
        next.videoType = 'composite'
      } else if (avatarMode && avatarAssetId) {
        next.videoType = 'avatar'
      } else {
        next.videoType = 'product'
      }
      // Carry forward the video mode chosen in StepIdea (StepSetup can override later)
      if (videoMode) {
        next.videoMode = videoMode
      }
      // Read bridgeEnabled directly from localStorage (same key as StepIdea's
      // useLocalStorageState so we pick up whatever the user toggled in step 1).
      try {
        const stored = localStorage.getItem('creativeos.createAd.bridgeEnabled')
        if (stored !== null) {
          next.bridgeEnabled = JSON.parse(stored) === true
        }
      } catch {
        // silently keep prev.bridgeEnabled
      }
      return next
    })
    goTo(2, id)
  }

  const handleApproved = () => goTo(3)
  const handleSetupNext = () => goTo(4)

  // True when there's an active draft worth offering "Start over" for.
  // Cheap on every render — checks two strings, no JSON parse. Avoids
  // showing the button on a truly fresh /create visit so it doesn't read
  // as scary.
  const hasDraftToDiscard = React.useMemo(() => {
    if (typeof window === 'undefined') return false
    try {
      const idea = window.localStorage.getItem('creativeos.createAd.idea')
      const ids = window.localStorage.getItem('creativeos.createAd.refAssetIds')
      return Boolean((idea && idea.length > 2) || (ids && ids !== '[]'))
    } catch {
      return false
    }
    // step is intentionally in deps so the button re-evaluates when the
    // user actually moves through the wizard (typing in step 1 won't
    // re-evaluate without it, but each step transition will).
  }, [step])

  const handleStartOver = () => {
    const ok = window.confirm(
      'Discard the current draft (idea, setup, references) and start fresh?',
    )
    if (!ok) return
    clearCreateAdDraft()
    // Reset the in-memory setup too — clearCreateAdDraft only wipes
    // localStorage; React state needs an explicit reset to take effect now.
    setSetup(DEFAULT_SETUP)
    setScriptTotalDuration(0)
    // Navigate back to a clean step-1 URL so any script_id query drops away.
    setSearchParams({ step: '1' })
    // Force StepIdea to remount so its useLocalStorageState initializers
    // re-read (now-empty) localStorage. Easiest way: a tiny key tick.
    window.location.reload()
  }

  const activeStepIndex = STEPS.findIndex((s) => s.id === step)

  return (
    <div className="animate-fade-in space-y-8">
      <header className="space-y-1">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Create Ad</h1>
          {hasDraftToDiscard && (
            <button
              type="button"
              onClick={handleStartOver}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
              title="Discard the saved draft and start a new ad from scratch"
            >
              Start over
            </button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Describe, script, setup, and generate — all in one flow.
        </p>
      </header>

      {/* Stepper — sticky. Each step button has a Tooltip explaining what
          happens at that step (Task 9). Tooltip works on hover regardless
          of whether the step is reachable — non-clickable future steps still
          get the explanatory hint so users can plan ahead. */}
      <nav aria-label="Ad creation steps" className="sticky top-0 z-10 bg-background pb-4 pt-2 -mx-4 px-4 border-b border-border">
        <TooltipProvider delayDuration={250}>
          <ol className="flex items-center gap-0">
            {STEPS.map((s, idx) => {
              const isComplete = activeStepIndex > idx
              const isActive = activeStepIndex === idx
              const isLast = idx === STEPS.length - 1

              return (
                <li key={s.id} className="flex items-center flex-1 min-w-0">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => { if (isComplete) goTo(s.id) }}
                        className={cn(
                          'flex items-center gap-2 shrink-0 transition-colors',
                          isComplete ? 'cursor-pointer' : 'cursor-default',
                        )}
                        aria-current={isActive ? 'step' : undefined}
                        aria-disabled={!isComplete}
                        // Keep Radix Tooltip's hover detection working (which
                        // requires a non-disabled focusable element) while
                        // preventing Tab from landing on unreachable steps —
                        // matches the accessibility behaviour the old
                        // disabled attribute provided.
                        tabIndex={isComplete ? 0 : -1}
                      >
                        <span
                          className={cn(
                            'flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold transition-colors',
                            isComplete
                              ? 'border-foreground bg-foreground text-background'
                              : isActive
                              ? 'border-foreground bg-transparent text-foreground'
                              : 'border-border bg-transparent text-muted-foreground',
                          )}
                        >
                          {isComplete ? <CheckCircle2 className="h-4 w-4" /> : idx + 1}
                        </span>
                        <span
                          className={cn(
                            'text-sm font-medium hidden sm:block',
                            isActive ? 'text-foreground'
                              : isComplete ? 'text-muted-foreground'
                              : 'text-muted-foreground/50',
                          )}
                        >
                          {s.label}
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                      <p className="font-semibold mb-0.5">Step {s.id}: {s.label}</p>
                      <p>{s.hint}</p>
                    </TooltipContent>
                  </Tooltip>

                  {!isLast && (
                    <div
                      className={cn('flex-1 mx-3 h-px transition-colors', isComplete ? 'bg-foreground' : 'bg-border')}
                    />
                  )}
                </li>
              )
            })}
          </ol>
        </TooltipProvider>
      </nav>

      {/* Step content */}
      <div>
        {step === 1 && (
          <StepIdea
            key={wizardKey}
            isQuickClip={false}
            onModeChange={() => {}}
            onScriptCreated={handleScriptCreated}
            storyboardPlan={storyboardPlan}
          />
        )}

        {step === 2 && scriptId !== null && (
          <StepScript
            scriptId={scriptId}
            onApproved={handleApproved}
            onDirtyChange={handleScriptDirtyChange}
          />
        )}
        {step === 2 && scriptId === null && (
          <p className="text-sm text-destructive" role="alert">
            Script ID missing — please restart from Step 1.
          </p>
        )}

        {step === 3 && (
          <StepSetup values={setup} onChange={setSetup} onNext={handleSetupNext} />
        )}

        {step === 4 && scriptId !== null && (
          <StepGenerate
            scriptId={scriptId}
            modelId={setup.modelId}
            aspectRatio={setup.aspectRatio}
            avatarAssetId={setup.avatarAssetId}
            refImageIds={setup.refImageIds}
            contextFolderIds={setup.contextFolderIds}
            styleReferenceIds={setup.styleReferenceIds}
            generationMode={setup.generationMode}
            videoMode={setup.videoMode}
            autoStitch={setup.autoStitch}
            transitionSeconds={setup.transitionSeconds}
            fastPreview={setup.fastPreview}
            bridgeEnabled={setup.bridgeEnabled}
            audioUrl={setup.audioUrl}
            voiceId={setup.voiceId}
            durationSeconds={scriptTotalDuration > 0 ? scriptTotalDuration : setup.durationSeconds}
            generationParams={setup.generationParams}
          />
        )}
        {step === 4 && scriptId === null && (
          <p className="text-sm text-destructive" role="alert">
            Script ID missing — please restart from Step 1.
          </p>
        )}
      </div>
    </div>
  )
}
